import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  Chip,
  Stack,
  Typography,
} from "@mui/material";
import ExploreOutlinedIcon from "@mui/icons-material/ExploreOutlined";
import { motion } from "framer-motion";
import { copyTextToClipboard } from "../../utils/clipboard";
import { dateConverter } from "../../utils/dateConveter";
import {
  buildReferralLink,
  formatRewardsEarnedLabel,
  getReferralProgressState,
} from "../../utils/referralProgress";
import { investorLeads } from "../../services/investorLeads";
import { useSelector } from "react-redux";
import DataGridTable from "../../components/DataGridTabel";
import logsService from "../../services/logServices";
import InvestorActivityEventType from "../../utils/eventTypes";

const BRAND_CORAL = "#FF382D";
const BRAND_CORAL_DARK = "#d12f26";
const INVESTED_GREEN = "#1B7F4B";
const INVESTED_GREEN_BG = "#E8F7EE";
const KYC_BLUE = "#1565C0";
const KYC_BLUE_BG = "#E3F2FD";
const VOIDED_AMBER = "#B45309";
const VOIDED_AMBER_BG = "#FFF7ED";

const REFERRAL_STATUS_STYLES = {
  REGISTERED: {
    label: "Registered",
    color: "grey.700",
    bgcolor: "grey.100",
    borderColor: "grey.300",
  },
  KYC_VERIFIED: {
    label: "KYC Verified",
    color: KYC_BLUE,
    bgcolor: KYC_BLUE_BG,
    borderColor: "rgba(21, 101, 192, 0.25)",
  },
  INVESTED: {
    label: "Invested",
    color: INVESTED_GREEN,
    bgcolor: INVESTED_GREEN_BG,
    borderColor: "rgba(27, 127, 75, 0.25)",
  },
  REWARDED: {
    label: "Rewarded",
    color: BRAND_CORAL,
    bgcolor: "rgba(255, 56, 45, 0.08)",
    borderColor: "rgba(255, 56, 45, 0.25)",
  },
  VOIDED: {
    label: "Voided",
    color: VOIDED_AMBER,
    bgcolor: VOIDED_AMBER_BG,
    borderColor: "rgba(180, 83, 9, 0.28)",
  },
};

const REWARD_TEXT = "25 sq. ft. per 5 unique investor investments";

function StatusBadge({ status }) {
  const statusStyle = REFERRAL_STATUS_STYLES[status] || {
    label: status || "—",
    color: "grey.700",
    bgcolor: "transparent",
    borderColor: "grey.300",
  };

  return (
    <Chip
      label={statusStyle.label}
      size="small"
      sx={{
        fontWeight: 700,
        fontSize: "0.6875rem",
        letterSpacing: "0.04em",
        height: 26,
        borderRadius: 999,
        bgcolor: statusStyle.bgcolor,
        color: statusStyle.color,
        border: "1px solid",
        borderColor: statusStyle.borderColor,
      }}
    />
  );
}

function SummaryCard() {
  const user = useSelector(state => state?.user?.data)
  const [progress,setProgress] = useState(0);
  const [goalStep,setGoalStep] = useState(5);
  const [campaignName,setCampaignName] = useState("");
  const getInvestedInvestorLeadsData = async () => {
    const res = await investorLeads.getInvested(user?._id);
    if (res&&res.success) {
      setProgress(res.investedLeads||0);
      setGoalStep(res.requiredReferrals || 5);
      setCampaignName(res?.listing?.title);
    }
  }
  useEffect(() => {
    if (user?._id) {
      getInvestedInvestorLeadsData();
    }
  }, [user?._id]);
  const {
    progress: investedCount,
    displayGoal,
    progressPercent,
    remaining,
    rewardsEarned,
  } = getReferralProgressState(progress, goalStep);

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 3,
        border: "1px solid",
        borderColor: "grey.200",
        boxShadow: "0 12px 32px rgba(33, 43, 54, 0.06)",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: { xs: "stretch", md: "center" },
          justifyContent: "space-between",
          gap: 3,
          p: { xs: 2.5, md: 3 },
        }}
      >
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="overline"
            sx={{
              color: BRAND_CORAL,
              fontWeight: 700,
              letterSpacing: "0.08em",
            }}
          >
            Active Campaign
          </Typography>
          <Typography
            sx={{
              mt: 0.5,
              fontSize: { xs: "1.25rem", md: "1.5rem" },
              fontWeight: 700,
              color: "text.primary",
              lineHeight: 1.25,
            }}
          >
            Campaign: {campaignName}
          </Typography>
          <Typography
            variant="body2"
            sx={{ mt: 0.75, color: "text.secondary", maxWidth: 520 }}
          >
            Reward: {REWARD_TEXT}
          </Typography>
        </Box>

        <Box
          sx={{
            width: { xs: "100%", md: 280 },
            flexShrink: 0,
            p: 2,
            borderRadius: 2.5,
            bgcolor: "grey.50",
            border: "1px solid",
            borderColor: "grey.200",
          }}
        >
          <Typography
            variant="body2"
            sx={{ fontWeight: 700, color: "text.primary", mb: 0.25 }}
          >
            {investedCount} / {displayGoal} Invested
          </Typography>
          <Typography
            variant="caption"
            sx={{ display: "block", mb: 1, color: BRAND_CORAL, fontWeight: 600 }}
          >
            {formatRewardsEarnedLabel(rewardsEarned)}
          </Typography>
          <Box
            sx={{
              height: 10,
              borderRadius: 999,
              bgcolor: "grey.200",
              overflow: "hidden",
            }}
          >
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${progressPercent}%` }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              style={{
                height: "100%",
                borderRadius: 999,
                backgroundColor: BRAND_CORAL,
              }}
            />
          </Box>
          <Typography variant="caption" sx={{ display: "block", mt: 1, color: "text.secondary" }}>
            {remaining} more unique investor investments to unlock your next reward
          </Typography>
        </Box>
      </Box>
    </Card>
  );
}

function ReferralRosterTable({ rows }) { 
  const columns = [
    { field: "name", headerName: "Name", flex: 1, minWidth: 150 },
    {
      field: "contactInfo",
      headerName: "Contact Info",
      flex: 1,
      minWidth: 180,
      sortable: false,
      renderCell: (params) => (
        <Stack spacing={0.25} sx={{ py: 1 }}>
          <Typography variant="body2" sx={{ color: "text.primary", lineHeight: 1.4 }}>
            {params.row.email}
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", lineHeight: 1.4 }}>
            {params.row.mobile}
          </Typography>
        </Stack>
      ),
    },
    {
      field: "created_at",
      headerName: "Date Joined",
      flex: 1,
      minWidth: 140,
      renderCell: (params) =>
        dateConverter({
          value: params.value,
          momentIsTrue: true,
          dateFormat: "DD MMM YYYY",
        }),
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      minWidth: 130,
      renderCell: (params) => <StatusBadge status={params.value} />,
    },
  ];
  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 3,
        border: "1px solid",
        borderColor: "grey.200",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          px: { xs: 2, md: 3 },
          py: 2,
          borderBottom: "1px solid",
          borderColor: "grey.200",
        }}
      >
        <Typography sx={{ fontWeight: 700, fontSize: "1.05rem" }}>
          Invited Friends
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.25 }}>
          Track who joined and who has invested through your link.
        </Typography>
      </Box>

      <DataGridTable 
        initialColumns={columns} 
        initialRows={rows} 
        initialPageSize={10}
        initialRowHeight={56}
        hideToolbar
      />
    </Card>
  );
}

function EmptyReferralState({ onCopyLink, copied, canCopy }) {
  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 3,
        border: "1px solid",
        borderColor: "grey.200",
        px: { xs: 3, md: 6 },
        py: { xs: 6, md: 8 },
        textAlign: "center",
      }}
    >
      <Box
        sx={{
          width: 72,
          height: 72,
          mx: "auto",
          mb: 2,
          borderRadius: "50%",
          display: "grid",
          placeItems: "center",
          bgcolor: "rgba(255, 56, 45, 0.08)",
          color: BRAND_CORAL,
        }}
      >
        <ExploreOutlinedIcon sx={{ fontSize: 36 }} />
      </Box>
      <Typography sx={{ fontWeight: 700, fontSize: "1.125rem", mb: 1 }}>
        You haven&apos;t invited anyone yet
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: "text.secondary",
          maxWidth: 420,
          mx: "auto",
          mb: 3,
          lineHeight: 1.6,
        }}
      >
        Start sharing your link to earn free land!
      </Typography>
      <Button
        variant="contained"
        onClick={onCopyLink}
        disabled={!canCopy}
        sx={{
          bgcolor: BRAND_CORAL,
          fontWeight: 700,
          textTransform: "none",
          borderRadius: 2,
          px: 3,
          boxShadow: "none",
          "&:hover": {
            bgcolor: BRAND_CORAL_DARK,
            boxShadow: "none",
          },
        }}
      >
        {copied ? "Copied!" : "Copy Invite Link"}
      </Button>
    </Card>
  );
}

export default function ReferralTrackingPage() {
  const user = useSelector(state => state?.user?.data)
  const [referrals,setReferrals] = useState([]);
  const [referralLink, setReferralLink] = useState("");
  const [copied, setCopied] = useState(false);

  const getInvestorLeadsData = async () => {
    try {
      const res = await investorLeads.all(user?._id);
      if (res&&res.success) {
        setReferrals(res.data||[]);
      }
    } catch (error) {
      console.log(error);
    }
  }

  const getReferralLinkData = async () => {
    try {
      const res = await investorLeads.getInvested(user?._id);
      if (res && res.success) {
        setReferralLink(buildReferralLink(res?.couponData));
      }
    } catch (error) {
      console.log(error);
    }
  }

  useEffect(() => {
    if (user?._id) {
      getInvestorLeadsData();
      getReferralLinkData();
    }
  }, [user?._id]);

  const handleCopyLink = useCallback(async () => {
    if (!referralLink?.trim()) return;

    try {
      await copyTextToClipboard(referralLink);
      setCopied(true);
      logsService.addLog({
        userId: user?._id,
        event: InvestorActivityEventType.SHARE_LINK_COPIED
      })
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  }, [referralLink, user?._id]);

  return (
    <Box sx={{ maxWidth: 1080, mx: "auto", pb: { xs: 10, md: 4 } }}>

      <Typography
        sx={{
          fontSize: { xs: "1.5rem", md: "1.875rem" },
          fontWeight: 700,
          color: "text.primary",
          my: 3,
          letterSpacing: "-0.02em",
        }}
      >
        Referral Rewards Tracking
      </Typography>

      <Stack spacing={3}>
        <SummaryCard />

        {referrals?.length > 0 ? (
          <ReferralRosterTable rows={referrals} />
        ) : (
          <EmptyReferralState
            onCopyLink={handleCopyLink}
            copied={copied}
            canCopy={Boolean(referralLink?.trim())}
          />
        )}
      </Stack>
    </Box>
  );
}
